import React, { useMemo } from "react";
import PropTypes from "kf-proptypes/widgets";

import { LookupCard, LOOKUP_CARD_SIZE } from "./lookupcard.js";
import { FieldLabel, FieldHint, FieldErrors } from "../fields/index.jsx";
import { LOOKUP_SIZE, LOOKUP_TYPE } from "./constants.js";
import { ReadonlyLookup } from "./readonly.jsx";
import { EditableLookup } from "./editable.jsx";

export { LookupCard, LOOKUP_CARD_SIZE, LOOKUP_SIZE, LOOKUP_TYPE as LookupType };

export function Lookup({
  handleChange,
  value,
  readOnly,
  disabled,
  position,
  className,
  size = LOOKUP_SIZE.BASE,
  // sourceUrl,
  isOpen,
  styleType,
  isError,
  queryDefinition,
  autoComplete,
  field,
  fieldConfig,
  customProps,
  columnLayout,
  id,
  label,
  required,
  hint,
  labelLeftIcon,
  labelRightIcon,
  errors = [],
  defer = false
}) {
  const autoFill = useMemo(
    function updateAutoAutoFill() {
      const queryDefId = queryDefinition && queryDefinition.getValue("Id");
      queryDefinition &&
        queryDefinition.getObject(queryDefId).getValue("AutoFill");
    },
    [queryDefinition]
  );

  return (
    <React.Fragment>
      {label && (
        <FieldLabel
          label={label}
          required={required}
          targetId={id}
          disabled={disabled}
          leftIcon={labelLeftIcon}
          rightIcon={labelRightIcon}
          size={size}
        />
      )}
      {readOnly || autoFill ? (
        <ReadonlyLookup
          value={value}
          className={className}
          size={size}
          isError={isError}
          queryDefinition={queryDefinition}
          customProps={customProps}
          columnLayout={columnLayout}
          errors={errors}
        />
      ) : (
        <EditableLookup
          handleChange={handleChange}
          value={value}
          className={className}
          size={size}
          isError={isError}
          queryDefinition={queryDefinition}
          customProps={customProps}
          columnLayout={columnLayout}
          errors={errors}
          disabled={disabled}
          position={position}
          isOpen={isOpen}
          styleType={styleType}
          autoComplete={autoComplete}
          field={field}
          fieldConfig={fieldConfig}
          defer={defer}
        />
      )}
      {hint && <FieldHint hint={hint} targetId={id} />}
      {errors && errors.length > 0 && (
        <FieldErrors errors={errors} targetId={id} />
      )}
    </React.Fragment>
  );
}

Lookup.propTypes = {
  /** Triggers when the value of the lookup field change */
  handleChange: PropTypes.func,
  /** Denotes the current value in the lookup */
  value: PropTypes.string,
  /** Defines the readonly state */
  readOnly: PropTypes.bool,
  /** Defines the disabled state */
  disabled: PropTypes.bool,
  /** Defines the className for custom styles */
  className: PropTypes.string,
  /** Defines the size of the lookup */
  size: PropTypes.string,
  /** Defines the position of the dropdown */
  position: PropTypes.string,
  /** Url from which the dropdown values of the loopup fetched */
  sourceUrl: PropTypes.string,
  /** Defines the field details of the current field in the form */
  field: PropTypes.shape({
    Column: PropTypes.string,
    CreatedAt: PropTypes.string,
    Id: PropTypes.string,
    Kind: PropTypes.string,
    Model: PropTypes.string,
    Name: PropTypes.string,
    Type: PropTypes.string
  }),
  /** configuration of the field */
  fieldConfig: PropTypes.shape({
    appearance: PropTypes.shape({
      style: PropTypes.objectOf(
        PropTypes.shape({
          ref: PropTypes.string
        })
      ),
      type: PropTypes.string
    }),
    columnId: PropTypes.string,
    id: PropTypes.string,
    isExpression: PropTypes.bool,
    isReadOnly: PropTypes.bool,
    name: PropTypes.string,
    permission: PropTypes.string,
    sourceUrl: PropTypes.func,
    type: PropTypes.string
  }),
  /** Custom props which includes the baseUrl */
  customProps: PropTypes.shape({
    activityInstanceId: PropTypes.string,
    adminBaseUrl: PropTypes.string,
    baseUrl: PropTypes.string,
    currentStep: PropTypes.string,
    flowId: PropTypes.string,
    flowType: PropTypes.string,
    idp: PropTypes.string, // Adjust the type based on the actual data type
    instanceId: PropTypes.string,
    isAdmin: PropTypes.bool,
    isSendBackToInitiator: PropTypes.bool, // Adjust the type based on the actual data type
    isStartStep: PropTypes.bool,
    parentInstanceId: PropTypes.string,
    parentModelId: PropTypes.string,
    parentResourceId: PropTypes.string,
    sharedWith: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string.isRequired,
        Kind: PropTypes.string.isRequired,
        Name: PropTypes.string.isRequired
      })
    ),
    showChildTablePreview: PropTypes.bool,
    status: PropTypes.string,
    type: PropTypes.string
  }),
  /** Defines the opened state of the dropdown */
  isOpen: PropTypes.bool,
  /** Defines the type of dropdown */
  styleType: PropTypes.string,
  /** Denotes error state */
  isError: PropTypes.bool,
  /** Denotes the query queryDefenition */
  queryDefinition: PropTypes.shape({
    id: PropTypes.string,
    context: PropTypes.func,
    getObject: PropTypes.func,
    getValue: PropTypes.func
  }),
  /** Denifines whether the input box was autocomplete or not */
  autoComplete: PropTypes.string,
  /** Defines the columnLayout inside the dropdown card.Possible values ("e1", null)  */
  columnLayout: PropTypes.string,
  /** Unique id of the label */
  id: PropTypes.string,
  /** Label of the field */
  label: PropTypes.string,
  /** Defines this field is required or not */
  required: PropTypes.bool,
  /** Defines the hint of the string */
  hint: PropTypes.string,
  /** Defines the error strings */
  errors: PropTypes.arrayOf(PropTypes.string),
  /** Defines the defer of the dropdown */
  defer: PropTypes.bool,
  /** it will render left icon of label field */
  labelLeftIcon: PropTypes.node,
  /** it will render right icon of label field */
  labelRightIcon: PropTypes.node
};

Lookup.defaultProps = {
  styleType: LOOKUP_TYPE.NORMAL,
  size: LOOKUP_SIZE.BASE,
  errors: [],
  value: "",
  className: "",
  isError: false,
  queryDefinition: null,
  customProps: {},
  columnLayout: "",
  handleChange: () => {},
  autoComplete: false,
  fieldConfig: {},
  defer: false,
  readOnly: false
};
